import React from 'react';
import { IconType } from 'react-icons';
import {
  HiMoon,
  HiSun,
  HiChevronDown,
  HiArrowRight,
  HiCube,
  HiBolt,
  HiHeart,
  HiGlobeAlt,
  HiArrowsRightLeft,
  HiTrophy,
  HiHomeModern,
  HiShoppingCart,
  HiChatBubbleLeft,
  HiChevronLeft,
  HiChevronRight,
  HiBars3,
  HiXMark,
  HiArrowUpRight,
  HiCheckCircle,
  HiArrowLeft,
  HiStar,
  HiMagnifyingGlass,
} from 'react-icons/hi2';
import {
  FaLinkedin,
  FaYoutube,
  FaTwitter,
  FaUtensils,
  FaQuoteLeft,
  FaCar,
  FaMountain,
  FaTruck,
  FaCampground,
  FaMugHot,
  FaBox,
} from 'react-icons/fa6';
import { HiViewGrid } from 'react-icons/hi';
import { FaCircle } from 'react-icons/fa6';

// Icon name mapping to actual icon components
const iconMap = {
  // UI Icons
  moon: HiMoon,
  sun: HiSun,
  'chevron-down': HiChevronDown,
  'chevron-left': HiChevronLeft,
  'chevron-right': HiChevronRight,
  'arrow-left': HiArrowLeft,
  'arrow-right': HiArrowRight,
  'arrow-up-right': HiArrowUpRight,
  circle: FaCircle,
  'check-circle': HiCheckCircle,
  star: HiStar,
  menu: HiBars3,
  x: HiXMark,
  search: HiMagnifyingGlass,

  // Business Icons
  package: HiCube,
  zap: HiBolt,
  heart: HiHeart,
  globe: HiGlobeAlt,
  'arrow-right-left': HiArrowsRightLeft,
  award: HiTrophy,

  // Sector Icons
  car: FaCar,
  truck: FaTruck,
  home: HiHomeModern,
  mountain: FaMountain,
  tent: FaCampground,
  'shopping-cart': HiShoppingCart,
  'utensils-crossed': FaUtensils,
  coffee: FaMugHot,
  'grid-3x3': HiViewGrid,
  box: FaBox,

  // Social Icons
  linkedin: FaLinkedin,
  youtube: FaYoutube,
  twitter: FaTwitter,

  // Communication Icons
  'message-circle': HiChatBubbleLeft,
  quote: FaQuoteLeft,
} as const;

export type IconName = keyof typeof iconMap;

export interface IconProps {
  name: IconName;
  size?: number | string;
  className?: string;
  color?: string;
  'aria-label'?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
  role?: string;
}

/**
 * Icon component that provides a consistent interface for all icons in the application.
 * Replaces Lucide React with React Icons while maintaining the same visual appearance.
 * 
 * @param name - The name of the icon to render
 * @param size - Size of the icon (number for pixels, string for CSS units)
 * @param className - Additional CSS classes
 * @param color - Icon color (CSS color value)
 * @param aria-label - Accessible label for screen readers
 * @param aria-hidden - Whether to hide from screen readers
 * @param role - ARIA role attribute
 */
export const Icon: React.FC<IconProps> = ({
  name,
  size = 24,
  className = '',
  color,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden,
  role,
  ...props
}) => {
  const IconComponent = iconMap[name] as IconType;

  if (!IconComponent) {
    console.warn(`Icon "${name}" not found in icon map`);
    return null;
  }

  // Convert size to appropriate format
  const iconSize = typeof size === 'number' ? `${size}px` : size;

  // Convert aria-hidden to boolean if it's a string
  const ariaHiddenValue = typeof ariaHidden === 'string' ? ariaHidden === 'true' : ariaHidden;

  return (
    <IconComponent
      className={className}
      style={{
        width: iconSize,
        height: iconSize,
        color: color,
      }}
      aria-label={ariaLabel}
      aria-hidden={ariaHiddenValue}
      role={role}
      {...props}
    />
  );
};

export default Icon;